
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import InternalLayout from '@/components/internal/InternalLayout';
import { 
  CreditCard, 
  Apple, 
  Gift, 
  Percent,
  Clock,
  MapPin,
  Check
} from 'lucide-react';

const Checkout = () => {
  const [appliedCredits, setAppliedCredits] = useState(0);
  const [giftCardCode, setGiftCardCode] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('apple');

  const treatment = {
    name: 'Botox - Forehead & Crow\'s Feet',
    price: 275,
    duration: '30 minutes',
    location: 'JUV Studio Beverly Hills'
  };

  const creditBalance = 127.50;
  const maxCreditsToApply = Math.min(creditBalance, treatment.price);
  const subtotal = treatment.price - appliedCredits;
  const taxes = subtotal * 0.0875; // 8.75% tax
  const total = subtotal + taxes;

  const handleApplyCredits = (amount: number) => {
    setAppliedCredits(Math.min(amount, maxCreditsToApply));
  };

  return (
    <InternalLayout 
      title="Checkout"
      subtitle="Complete your booking"
      showBackButton
      backPath="/booking"
    >
      <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Payment Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Treatment Summary */}
          <Card className="bg-white shadow-luxury" style={{ borderColor: 'rgba(205, 167, 157, 0.3)' }}>
            <CardHeader>
              <CardTitle className="font-juv-heading" style={{ color: '#5C322B' }}>Treatment Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold" style={{ color: '#5C322B' }}>{treatment.name}</h3>
                  <div className="flex items-center space-x-4 mt-2 text-sm" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      {treatment.duration}
                    </div>
                    <div className="flex items-center">
                      <MapPin className="h-4 w-4 mr-1" />
                      {treatment.location}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold" style={{ color: '#5C322B' }}>${treatment.price}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* JUV Credits */}
          <Card className="shadow-luxury" style={{ 
            background: 'linear-gradient(to bottom right, rgba(242, 217, 211, 0.3), #ffffff)',
            borderColor: 'rgba(205, 167, 157, 0.3)'
          }}>
            <CardHeader>
              <CardTitle className="font-juv-heading flex items-center" style={{ color: '#5C322B' }}>
                <Gift className="h-5 w-5 mr-2" style={{ color: '#A55C52' }} />
                JUV Credits
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p style={{ color: '#5C322B' }}>Available Balance</p>
                  <p className="text-2xl font-bold" style={{ color: '#A55C52' }}>${creditBalance.toFixed(2)}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>Applied</p>
                  <p className="text-xl font-bold" style={{ color: '#5C322B' }}>${appliedCredits.toFixed(2)}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleApplyCredits(25)}
                  className="hover:bg-white/80"
                  style={{ borderColor: 'rgba(205, 167, 157, 0.5)', color: '#5C322B' }}
                >
                  $25
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleApplyCredits(50)}
                  className="hover:bg-white/80"
                  style={{ borderColor: 'rgba(205, 167, 157, 0.5)', color: '#5C322B' }}
                >
                  $50
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleApplyCredits(maxCreditsToApply)}
                  className="hover:bg-white/80"
                  style={{ borderColor: 'rgba(205, 167, 157, 0.5)', color: '#5C322B' }}
                >
                  Max
                </Button>
              </div>

              {appliedCredits > 0 && (
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                  <span style={{ color: '#5C322B' }}>Credits Applied</span>
                  <span className="font-medium" style={{ color: '#A55C52' }}>-${appliedCredits.toFixed(2)}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Gift Card */}
          <Card className="bg-white shadow-luxury" style={{ borderColor: 'rgba(205, 167, 157, 0.3)' }}>
            <CardHeader>
              <CardTitle className="font-juv-heading flex items-center" style={{ color: '#5C322B' }}>
                <Percent className="h-5 w-5 mr-2" style={{ color: '#A55C52' }} />
                Gift Card or Promo Code
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex space-x-2">
                <Input
                  placeholder="Enter code"
                  value={giftCardCode}
                  onChange={(e) => setGiftCardCode(e.target.value)}
                  className="border-2 focus:ring-0"
                  style={{ borderColor: 'rgba(205, 167, 157, 0.5)' }}
                />
                <Button 
                  variant="outline"
                  className="hover:bg-white/80"
                  style={{ borderColor: 'rgba(205, 167, 157, 0.5)', color: '#5C322B' }}
                >
                  Apply
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Payment Method */}
          <Card className="bg-white shadow-luxury" style={{ borderColor: 'rgba(205, 167, 157, 0.3)' }}>
            <CardHeader>
              <CardTitle className="font-juv-heading" style={{ color: '#5C322B' }}>Payment Method</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <label className="flex items-center space-x-3 p-4 border rounded-lg cursor-pointer hover:bg-white/80" style={{ borderColor: 'rgba(205, 167, 157, 0.3)' }}>
                  <input
                    type="radio"
                    name="payment"
                    value="apple"
                    checked={paymentMethod === 'apple'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    style={{ accentColor: '#A55C52' }}
                  />
                  <Apple className="h-5 w-5" />
                  <span className="font-medium" style={{ color: '#5C322B' }}>Apple Pay</span>
                </label>

                <label className="flex items-center space-x-3 p-4 border rounded-lg cursor-pointer hover:bg-white/80" style={{ borderColor: 'rgba(205, 167, 157, 0.3)' }}>
                  <input
                    type="radio"
                    name="payment"
                    value="card"
                    checked={paymentMethod === 'card'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    style={{ accentColor: '#A55C52' }}
                  />
                  <CreditCard className="h-5 w-5" />
                  <span className="font-medium" style={{ color: '#5C322B' }}>Card on File</span>
                  <Badge variant="secondary" className="ml-auto" style={{ backgroundColor: 'rgba(242, 217, 211, 0.5)', color: '#5C322B' }}>•••• 4242</Badge>
                </label>

                <label className="flex items-center space-x-3 p-4 border rounded-lg cursor-pointer hover:bg-white/80" style={{ borderColor: 'rgba(205, 167, 157, 0.3)' }}>
                  <input
                    type="radio"
                    name="payment"
                    value="affirm"
                    checked={paymentMethod === 'affirm'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    style={{ accentColor: '#A55C52' }}
                  />
                  <span className="font-medium" style={{ color: '#5C322B' }}>Split with Affirm</span>
                  <Badge variant="outline" className="ml-auto" style={{ borderColor: 'rgba(205, 167, 157, 0.5)', color: '#5C322B' }}>4 payments</Badge>
                </label>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <Card className="bg-white shadow-luxury sticky top-6" style={{ borderColor: 'rgba(205, 167, 157, 0.3)' }}>
            <CardHeader>
              <CardTitle className="font-juv-heading" style={{ color: '#5C322B' }}>Order Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span style={{ color: '#5C322B' }}>Subtotal</span>
                  <span style={{ color: '#5C322B' }}>${treatment.price.toFixed(2)}</span>
                </div>
                
                {appliedCredits > 0 && (
                  <div className="flex justify-between" style={{ color: '#A55C52' }}>
                    <span>JUV Credits</span>
                    <span>-${appliedCredits.toFixed(2)}</span>
                  </div>
                )}
                
                <div className="flex justify-between text-sm" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>
                  <span>Taxes</span>
                  <span>${taxes.toFixed(2)}</span>
                </div>
                
                <Separator style={{ backgroundColor: 'rgba(205, 167, 157, 0.3)' }} />
                
                <div className="flex justify-between text-lg font-bold" style={{ color: '#5C322B' }}>
                  <span>Total</span>
                  <span>${total.toFixed(2)}</span>
                </div>
              </div>

              <Button 
                className="w-full text-white hover:opacity-90 transition-all duration-300 sticky bottom-6"
                style={{ background: 'linear-gradient(135deg, #A55C52, #5C322B)' }}
                size="lg"
              >
                <Check className="h-5 w-5 mr-2" />
                Complete Booking
              </Button>

              <p className="text-xs text-center" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>
                By completing this booking, you agree to our terms of service and cancellation policy.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </InternalLayout>
  );
};

export default Checkout;
